import React, { FC, useState } from 'react';

import {
  Box,
  Button,
  Grid,
  HStack,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { customTheme } from 'theme';

import { Tag } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { PageInfo } from 'components/Navigation';
import {
  getHighPrivilegeColor,
  renderHighPrivilege,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';

import { DetailsSection } from './DetailsSection';
import { GrantingRoles, ROLE_PAGE_SIZES } from './GrantingRoles';
import {
  DataAccessIdentity,
  useDataAccessTableDetails,
} from './useDataAccessQueries';
import { DataAccessRow, formatTag, getErrorMessage } from './utils';

// Lines up with the table's name, two levels down.
const INDENT = `${16 + 28 + 2 * 20}px`;

interface TableDetailsProps {
  identity: DataAccessIdentity;
  table: DataAccessRow;
}

/**
 * What the identity can do on one table, read live when it is opened: the
 * exact permissions by type, who grants them, and the table's owner and tags.
 */
export const TableDetails: FC<TableDetailsProps> = ({ identity, table }) => {
  const [rolesPage, setRolesPage] = useState<PageInfo>({
    page_number: 1,
    page_size: ROLE_PAGE_SIZES[0],
  });
  const query = useDataAccessTableDetails(identity, table.uuid, rolesPage);
  const { data } = query;

  const body = () => {
    if (query.isError && !data) {
      // The identity can no longer reach the table, for example after a grant
      // was revoked since the list loaded.
      const gone = (query.error as any)?.response?.status === 404;
      return (
        <HStack spacing={3}>
          <Text color="critical">
            {gone
              ? 'This user can no longer access this table'
              : getErrorMessage(query.error, 'Failed to load table details')}
          </Text>
          {!gone && (
            <Button size="xs" variant="outline" onClick={() => query.refetch()}>
              Retry
            </Button>
          )}
        </HStack>
      );
    }
    if (!data) return <CustomSpinner size="sm" justify="flex-start" />;

    const ownerAndTags = data.owner_and_tags_available ?? true;
    const tags = (data.tags ?? []).map(tag => formatTag(tag.key, tag.value));
    const roleTotal = data.granting_roles.total ?? 0;
    const unavailable = (
      <Box color="gray.400">Unavailable until a collection completes</Box>
    );

    return (
      <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={6}>
        <DetailsSection
          title={`PERMISSIONS: ${data.permission_count.toLocaleString()}`}
        >
          <Stack spacing={1}>
            {data.privileges.map(privilege => (
              <HStack key={privilege.privilege_level} align="flex-start">
                <HStack minW={44} flexShrink={0} spacing={2}>
                  {renderHighPrivilege(
                    privilege.privilege_level,
                    getHighPrivilegeColor(privilege.privilege_level),
                  )}
                  <Box fontWeight={500}>{privilege.privilege_level}</Box>
                </HStack>
                <Box>{privilege.permissions.join(', ')}</Box>
              </HStack>
            ))}
          </Stack>
        </DetailsSection>
        <DetailsSection
          title={`GRANTED BY: ${roleTotal + (data.has_direct_grant ? 1 : 0)}`}
        >
          <GrantingRoles
            roles={data.granting_roles}
            direct={data.has_direct_grant}
            pageInfo={rolesPage}
            onPageChange={setRolesPage}
          />
        </DetailsSection>
        <DetailsSection title="OWNER">
          {ownerAndTags ? data.owner ?? '-' : unavailable}
        </DetailsSection>
        <DetailsSection title="TAGS">
          {!ownerAndTags ? (
            unavailable
          ) : tags.length ? (
            // Every tag, unlike a table cell's first three.
            <Wrap spacing={1}>
              {tags.map(tag => (
                <WrapItem key={tag}>
                  <Tag label={tag} />
                </WrapItem>
              ))}
            </Wrap>
          ) : (
            '-'
          )}
        </DetailsSection>
      </Grid>
    );
  };

  return (
    // Zero width while the table sizes its columns, so long permission lists
    // wrap instead of widening the table.
    <Box w={0} minW="full">
      <Box
        pl={INDENT}
        pr={4}
        py={4}
        bg={customTheme.colors.gray['50']}
        opacity={query.isPlaceholderData ? 0.6 : 1}
      >
        {body()}
      </Box>
    </Box>
  );
};
